import { supabase } from '../lib/supabase';

/**
 * Service for managing workout records (Personal Records + Progressive Overload)
 */
export const workoutRecordsService = {
  /**
   * Log a new workout record (set/rep/weight entry)
   */
  async logWorkoutRecord(userId, recordData) {
    const { data, error } = await supabase?.from('workout_records')?.insert({
        user_id: userId,
        exercise_name: recordData?.exerciseName,
        sets: recordData?.sets || 0,
        reps: recordData?.reps || 0,
        weight: recordData?.weight || 0,
        duration_minutes: recordData?.durationMinutes || 0,
        distance: recordData?.distance || 0,
        calories: recordData?.calories || 0,
        notes: recordData?.notes || '',
        workout_date: recordData?.date || new Date()?.toISOString()?.split('T')?.[0],
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Get all workout records for a user, optionally filtered by exercise
   */
  async getWorkoutRecords(userId, exerciseName = null) {
    let query = supabase?.from('workout_records')?.select('*')?.eq('user_id', userId)?.order('workout_date', { ascending: false });

    if (exerciseName) {
      query = query?.eq('exercise_name', exerciseName);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Get progression history for a specific exercise (for chart)
   */
  async getExerciseProgression(userId, exerciseName) {
    const { data, error } = await supabase?.from('workout_records')?.select('*')?.eq('user_id', userId)?.eq('exercise_name', exerciseName)?.order('workout_date', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get all-time personal records per exercise for a user
   * Returns best weight, best reps, best distance, best duration, best calories per exercise
   */
  async getPersonalRecords(userId) {
    const { data, error } = await supabase?.from('workout_records')?.select('*')?.eq('user_id', userId)?.order('workout_date', { ascending: false });

    if (error) throw error;

    const records = data || [];
    const prMap = {};

    for (const record of records) {
      const name = record?.exercise_name;
      if (!prMap?.[name]) {
        prMap[name] = {
          exerciseName: name,
          bestWeight: null,
          bestReps: null,
          bestDistance: null,
          bestDuration: null,
          bestCalories: null,
          totalSessions: 0,
        };
      }

      const pr = prMap?.[name];
      pr.totalSessions += 1;

      if (record?.weight > 0) {
        if (!pr?.bestWeight || record?.weight > pr?.bestWeight?.value) {
          pr.bestWeight = { value: record?.weight, date: record?.workout_date, sets: record?.sets, reps: record?.reps };
        }
      }
      if (record?.reps > 0) {
        if (!pr?.bestReps || record?.reps > pr?.bestReps?.value) {
          pr.bestReps = { value: record?.reps, date: record?.workout_date, sets: record?.sets, weight: record?.weight };
        }
      }
      if (record?.distance > 0) {
        if (!pr?.bestDistance || record?.distance > pr?.bestDistance?.value) {
          pr.bestDistance = { value: record?.distance, date: record?.workout_date };
        }
      }
      if (record?.duration_minutes > 0) {
        if (!pr?.bestDuration || record?.duration_minutes > pr?.bestDuration?.value) {
          pr.bestDuration = { value: record?.duration_minutes, date: record?.workout_date };
        }
      }
      if (record?.calories > 0) {
        if (!pr?.bestCalories || record?.calories > pr?.bestCalories?.value) {
          pr.bestCalories = { value: record?.calories, date: record?.workout_date };
        }
      }
    }

    return Object.values(prMap);
  },

  /**
   * Check if a new record breaks any existing PR for the user
   * Returns array of PR types broken: ['weight', 'reps', 'distance', 'duration', 'calories']
   */
  async checkForNewPR(userId, exerciseName, newRecord) {
    const { data, error } = await supabase?.from('workout_records')?.select('*')?.eq('user_id', userId)?.eq('exercise_name', exerciseName);

    if (error) throw error;

    const existing = data || [];
    const broken = [];

    if (newRecord?.weight > 0) {
      const maxWeight = Math.max(0, ...existing?.map(r => r?.weight || 0));
      if (newRecord?.weight > maxWeight) broken?.push('weight');
    }
    if (newRecord?.reps > 0) {
      const maxReps = Math.max(0, ...existing?.map(r => r?.reps || 0));
      if (newRecord?.reps > maxReps) broken?.push('reps');
    }
    if (newRecord?.distance > 0) {
      const maxDist = Math.max(0, ...existing?.map(r => r?.distance || 0));
      if (newRecord?.distance > maxDist) broken?.push('distance');
    }
    if (newRecord?.duration_minutes > 0) {
      const maxDur = Math.max(0, ...existing?.map(r => r?.duration_minutes || 0));
      if (newRecord?.duration_minutes > maxDur) broken?.push('duration');
    }
    if (newRecord?.calories > 0) {
      const maxCal = Math.max(0, ...existing?.map(r => r?.calories || 0));
      if (newRecord?.calories > maxCal) broken?.push('calories');
    }

    return broken;
  },

  /**
   * Get progressive overload suggestion for next session based on last N sessions
   * Returns suggested sets, reps, weight for next workout
   */
  getSuggestion(history) {
    if (!history || history?.length === 0) return null;

    const sorted = [...history]?.sort((a, b) => new Date(b.workout_date) - new Date(a.workout_date));
    const last = sorted?.[0];
    const prev = sorted?.[1];

    const suggestion = {
      sets: last?.sets || 3,
      reps: last?.reps || 10,
      weight: last?.weight || 0,
      note: '',
    };

    if (!prev) {
      // First session — suggest same + small increment
      if (last?.weight > 0) {
        suggestion.weight = last?.weight + 2.5;
        suggestion.note = `Try +2.5kg from your first session`;
      } else if (last?.reps > 0) {
        suggestion.reps = last?.reps + 1;
        suggestion.note = `Try +1 rep from your first session`;
      }
      return suggestion;
    }

    // Progressive overload logic
    const weightDiff = (last?.weight || 0) - (prev?.weight || 0);
    const repsDiff = (last?.reps || 0) - (prev?.reps || 0);

    if (last?.weight > 0) {
      if (weightDiff >= 0) {
        // Weight increased or stayed same — suggest +2.5kg
        suggestion.weight = (last?.weight || 0) + 2.5;
        suggestion.note = `+2.5kg from last session (${last?.weight}kg → ${suggestion?.weight}kg)`;
      } else {
        // Weight dropped — suggest same as last
        suggestion.weight = last?.weight;
        suggestion.note = `Maintain ${last?.weight}kg before increasing`;
      }
    } else if (last?.reps > 0) {
      if (repsDiff >= 0) {
        suggestion.reps = (last?.reps || 0) + 1;
        suggestion.note = `+1 rep from last session (${last?.reps} → ${suggestion?.reps} reps)`;
      } else {
        suggestion.reps = last?.reps;
        suggestion.note = `Maintain ${last?.reps} reps before increasing`;
      }
    }

    return suggestion;
  },

  /**
   * Delete a workout record
   */
  async deleteWorkoutRecord(recordId) {
    const { error } = await supabase?.from('workout_records')?.delete()?.eq('id', recordId);

    if (error) throw error;
  },

  /**
   * Get unique exercise names for a user
   */
  async getExerciseNames(userId) {
    const { data, error } = await supabase?.from('workout_records')?.select('exercise_name')?.eq('user_id', userId);

    if (error) throw error;
    const names = [...new Set((data || []).map(r => r?.exercise_name))];
    return names?.sort();
  },
};

export default workoutRecordsService;
